<div class="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-mb-4 tw-gap-2 sm:tw-gap-4">
  <h2 class="tw-text-lg tw-font-medium dark:tw-text-gray-300 tw-m-0">Chat History</h2>

  <div class="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
    <button
      mat-stroked-button
      color="primary"
      (click)="getChats(true)"
      [ngClass]="{ 'tw-bg-gray-200 dark:tw-bg-gray-500': chatsRefreshing }"
      [disabled]="chatsRefreshing"
      class="tw-flex-shrink-0">
      <mat-icon [ngClass]="{'rotate-icon': chatsRefreshing}">refresh</mat-icon>
      <span class="tw-hidden sm:tw-inline">Refresh</span>
    </button>

    <button
      *ngIf="isAdmin | async"
      mat-stroked-button
      color="primary"
      (click)="toggleAdminMode()"
      [ngClass]="{ 'tw-bg-blue-100 dark:tw-bg-blue-900': showAllChats }"
      class="tw-flex-shrink-0 tw-flex tw-items-center tw-gap-1">
      <mat-checkbox
        [checked]="showAllChats"
        (click)="$event.stopPropagation()"
        class="tw-pointer-events-none sm:tw-mr-1"></mat-checkbox>
      <mat-icon>construction</mat-icon>
      <span class="tw-hidden sm:tw-inline">Show All Orgs</span>
    </button>

    <button mat-icon-button (click)="onCloseDrawer()">
      <mat-icon class="dark:tw-text-gray-300">close</mat-icon>
    </button>
  </div>
</div>

<!-- Chat history list view -->
<div class="chat-history-list tw-overflow-y-auto">
  <!-- loading state msg -->
  <div
    *ngIf="chatsRefreshing"
    class="tw-text-center tw-py-3 tw-text-gray-500 dark:tw-text-gray-400 tw-flex tw-items-center tw-justify-center tw-gap-2">
    <span class="tw-text-sm tw-italic">Just a moment...</span>
  </div>

  <!-- empty state msg -->
  <div
    *ngIf="!chatsRefreshing && chatHistory.length === 0"
    class="tw-text-center tw-py-3 tw-text-gray-500 dark:tw-text-gray-400">
    No chat history available
  </div>

  <!-- Chat history items - only show when not loading or when we have items -->
  <div *ngIf="!chatsRefreshing && chatHistory.length > 0" class="tw-space-y-3 tw-px-1">
    <div
      *ngFor="let chat of chatHistory"
      class="chat-history-item tw-p-4 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-bg-white dark:tw-bg-gray-800 tw-cursor-pointer hover:tw-bg-gray-50 dark:hover:tw-bg-gray-700 tw-transition-colors tw-shadow-sm hover:tw-shadow"
      (click)="onRowClicked({data: chat})">
      <div class="tw-flex tw-justify-between tw-items-start tw-gap-4">
        <div class="tw-font-medium dark:tw-text-white tw-truncate tw-text-base">
          {{chat.summary || 'Untitled Chat'}}
          <span
            *ngIf="chat.status === 'busy' || !chat.summary"
            class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
            (thinking)</span
          >
        </div>
        <div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-min-w-20">
          {{(chat.updated_at || chat.created_at) | date:'MMM d, y'}}
        </div>
      </div>
      <div class="tw-flex tw-items-center tw-gap-4 tw-mt-2">
        <div
          class="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
          <mat-icon class="tw-text-[16px] tw-h-4 tw-w-4">question_answer</mat-icon>
          <span>{{getMessageCount(chat)}} messages</span>
        </div>

        <!-- Deep research indicator -->
        <div
          *ngIf="isDeepResearch(chat)"
          class="tw-flex tw-items-center tw-text-purple-600 dark:tw-text-purple-400"
          matTooltip="Deep research"
          matTooltipClass="tw-whitespace-pre-line">
          <mat-icon class="tw-text-[16px] tw-h-4 tw-w-4">travel_explore</mat-icon>
        </div>

        <div
          *ngIf="(isAdmin | async) && chat.stats?.total_cost != null"
          class="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
          <mat-icon class="tw-text-[16px] tw-h-4 tw-w-4">attach_money</mat-icon>
          <span>{{chat.stats.total_cost | number:'1.2-2'}}</span>
        </div>

        <!-- Feedback indicators (only shown in admin mode) -->
        <div *ngIf="showAllChats" class="tw-flex tw-items-center tw-gap-3 tw-ml-auto">
          <!-- Thumbs up with count -->
          <div
            class="tw-flex tw-items-center tw-gap-0.5 tw-text-xs"
            [ngClass]="{
              'tw-text-green-600 dark:tw-text-green-400': !chat.feedbackLoading && getPositiveFeedbackCount(chat) > 0,
              'tw-text-gray-400 dark:tw-text-gray-500': !chat.feedbackLoading && getPositiveFeedbackCount(chat) === 0
            }"
            [matTooltip]="getPositiveFeedbackTooltip(chat)"
            matTooltipClass="tw-whitespace-pre-line">
            <mat-icon class="tw-text-[16px] tw-h-4 tw-w-4">thumb_up</mat-icon>
            <span
              class="tw-font-medium"
              *ngIf="!chat.feedbackLoading"
              >{{getPositiveFeedbackCount(chat)}}</span
            >
            <mat-spinner *ngIf="chat.feedbackLoading" [diameter]="14"></mat-spinner>
          </div>

          <!-- Thumbs down with count -->
          <div
            class="tw-flex tw-items-center tw-gap-0.5 tw-text-xs"
            [ngClass]="{
              'tw-text-red-600 dark:tw-text-red-400': !chat.feedbackLoading && getNegativeFeedbackCount(chat) > 0,
              'tw-text-gray-400 dark:tw-text-gray-500': !chat.feedbackLoading && getNegativeFeedbackCount(chat) === 0
            }"
            [matTooltip]="getNegativeFeedbackTooltip(chat)"
            matTooltipClass="tw-whitespace-pre-line">
            <mat-icon class="tw-text-[16px] tw-h-4 tw-w-4">thumb_down</mat-icon>
            <span
              class="tw-font-medium"
              *ngIf="!chat.feedbackLoading"
              >{{getNegativeFeedbackCount(chat)}}</span
            >
            <mat-spinner *ngIf="chat.feedbackLoading" [diameter]="14"></mat-spinner>
          </div>
        </div>
      </div>
      <div class="tw-flex tw-justify-between tw-items-center tw-mt-2">
        <div
          *ngIf="chat.user_email"
          class="tw-hidden md:tw-block tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
          <span class="tw-italic">by {{chat.user_email}}</span>
        </div>
        <div
          *ngIf="showAllChats && chat.org_id"
          class="tw-ml-auto tw-px-2 tw-py-1 tw-rounded-md tw-bg-green-50 dark:tw-bg-green-900/30 tw-text-xs tw-font-medium tw-text-green-700 dark:tw-text-green-300">
          {{getOrgName(chat.org_id) | async}}
        </div>
      </div>
    </div>
  </div>
</div>
